/**
 * /app/positions — full Positions page.
 *
 * Two tabs:
 *   Open    — every currently-open position. Same shape as dashboard
 *             widget, but full width + click → detail drawer.
 *   Closed  — paginated history with filter bar
 *             (symbol / direction / exit_reason / days).
 *
 * Click on any row → opens the PositionDetailDrawer (same component
 * for both tabs).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Pagination } from '@/components/ui/Pagination'
import { PositionDetailDrawer } from '@/components/positions/PositionDetailDrawer'
import {
  type ClosedPosition,
  type OpenPosition,
  useClosedPositions,
  useOpenPositions,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatNumber, formatPct, formatUsd, timeAgo } from '@/lib/format'

const PAGE_SIZE = 50

type Tab = 'open' | 'closed'

export default function Positions() {
  const { t } = useTranslation('app')
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'closed' ? 'closed' : 'open'
  const [detailId, setDetailId] = useState<number | null>(null)

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params)
    p.set('tab', next)
    setParams(p, { replace: true })
  }

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('positions.title')}
        </h1>
        <p className="text-sm text-textDim mt-1">{t('positions.subtitle')}</p>
      </header>

      <div className="border-b border-line flex gap-1 mb-4">
        <TabButton active={tab === 'open'} onClick={() => setTab('open')}>
          {t('positions.tab.open')}
        </TabButton>
        <TabButton active={tab === 'closed'} onClick={() => setTab('closed')}>
          {t('positions.tab.closed')}
        </TabButton>
      </div>

      {tab === 'open' ? (
        <OpenTab onOpenDetail={setDetailId} />
      ) : (
        <ClosedTab onOpenDetail={setDetailId} />
      )}

      <PositionDetailDrawer
        positionId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  )
}

/* ─── Tabs ────────────────────────────────────────────────────────── */

function TabButton({
  active, onClick, children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm tracking-tight border-b-2 -mb-px transition-colors',
        active
          ? 'border-fire text-text'
          : 'border-transparent text-textDim hover:text-text',
      )}
    >
      {children}
    </button>
  )
}

/* ─── Open tab ────────────────────────────────────────────────────── */

function OpenTab({
  onOpenDetail,
}: {
  onOpenDetail: (id: number) => void
}) {
  const { t } = useTranslation('app')
  const { data, isLoading } = useOpenPositions()
  const items = data?.items

  if (isLoading || items === undefined) {
    return <Empty>—</Empty>
  }
  if (items.length === 0) {
    return <Empty>{t('dashboard.open_positions.empty')}</Empty>
  }

  return (
    <section className="bg-ink border border-line">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
            <tr>
              <Th className="text-left px-5">{t('dashboard.open_positions.table.symbol')}</Th>
              <Th className="text-left">{t('dashboard.open_positions.table.side')}</Th>
              <Th className="text-right">{t('dashboard.open_positions.table.size')}</Th>
              <Th className="text-right">{t('dashboard.open_positions.table.entry')}</Th>
              <Th className="text-right">{t('dashboard.open_positions.table.mark')}</Th>
              <Th className="text-right">{t('dashboard.open_positions.table.pnl')}</Th>
              <Th className="text-right">{t('dashboard.open_positions.table.age')}</Th>
              <Th className="text-right px-5">{t('dashboard.open_positions.table.dca')}</Th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {items.map((p) => (
              <OpenRow key={p.id} pos={p} onClick={() => onOpenDetail(p.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function OpenRow({
  pos, onClick,
}: {
  pos: OpenPosition
  onClick: () => void
}) {
  const { t } = useTranslation('app')
  const pnl = pos.unrealized_pnl
  const pct = pos.unrealized_pnl_pct
  const pnlTone = pnl == null
    ? 'text-textFaint'
    : pnl >= 0 ? 'text-pulse' : 'text-rose'
  return (
    <tr
      onClick={onClick}
      className="border-b border-line/40 hover:bg-elevated/30 cursor-pointer transition-colors"
    >
      <td className="px-5 py-2 font-semibold">{pos.symbol}</td>
      <td className="py-2">
        <span className={cn(
          'pill',
          pos.direction === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose',
        )}>
          {t(`dashboard.open_positions.side.${pos.direction}`)}
        </span>
      </td>
      <td className="text-right py-2">{formatNumber(pos.quantity, 4)}</td>
      <td className="text-right py-2 text-textDim">{formatNumber(pos.entry_price, 4)}</td>
      <td className="text-right py-2">
        {pos.current_price != null ? formatNumber(pos.current_price, 4) : '—'}
      </td>
      <td className={cn('text-right py-2', pnlTone)}>
        {pnl != null ? formatUsd(pnl, { sign: true }) : '—'}
        {pct != null && (
          <span className="ml-1 text-textFaint">
            {formatPct(pct, { sign: true })}
          </span>
        )}
      </td>
      <td className="text-right py-2 text-textFaint">{timeAgo(pos.opened_at)}</td>
      <td className="text-right px-5 py-2">
        {pos.dca_level > 0 ? (
          <span className="pill bg-fire-faint text-fire">DCA{pos.dca_level}</span>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>
    </tr>
  )
}

/* ─── Closed tab ──────────────────────────────────────────────────── */

const EXIT_REASONS = [
  'tp', 'sl', 'trailing', 'manual', 'liquidation', 'timeout',
] as const

function ClosedTab({
  onOpenDetail,
}: {
  onOpenDetail: (id: number) => void
}) {
  const { t } = useTranslation('app')

  const [symbol, setSymbol] = useState('')
  const [direction, setDirection] = useState<'' | 'long' | 'short'>('')
  const [exitReason, setExitReason] = useState('')
  const [days, setDays] = useState<number>(30)
  const [offset, setOffset] = useState(0)

  const filters = useMemo(() => ({
    limit: PAGE_SIZE,
    offset,
    symbol: symbol.trim().toUpperCase() || undefined,
    direction: direction || undefined,
    exit_reason: exitReason || undefined,
    days,
  }), [symbol, direction, exitReason, days, offset])

  const { data, isLoading, isFetching } = useClosedPositions(filters)

  // Any filter change resets offset to 0.
  const resetOffset = () => setOffset(0)

  return (
    <section>
      {/* Filter bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Input
          placeholder={t('positions.filter.symbol_placeholder')}
          value={symbol}
          onChange={(e) => { setSymbol(e.target.value); resetOffset() }}
        />
        <Select
          value={direction}
          onChange={(e) => {
            setDirection(e.target.value as '' | 'long' | 'short')
            resetOffset()
          }}
        >
          <option value="">{t('positions.filter.direction_all')}</option>
          <option value="long">{t('dashboard.open_positions.side.long')}</option>
          <option value="short">{t('dashboard.open_positions.side.short')}</option>
        </Select>
        <Select
          value={exitReason}
          onChange={(e) => { setExitReason(e.target.value); resetOffset() }}
        >
          <option value="">{t('positions.filter.exit_all')}</option>
          {EXIT_REASONS.map((r) => (
            <option key={r} value={r}>
              {t(`exit_reason.${r}`, { defaultValue: r })}
            </option>
          ))}
        </Select>
        <Select
          value={String(days)}
          onChange={(e) => { setDays(Number(e.target.value)); resetOffset() }}
        >
          <option value="7">{t('positions.filter.days', { count: 7 })}</option>
          <option value="30">{t('positions.filter.days', { count: 30 })}</option>
          <option value="90">{t('positions.filter.days', { count: 90 })}</option>
          <option value="365">{t('positions.filter.days', { count: 365 })}</option>
        </Select>
      </div>

      <div className="bg-ink border border-line">
        {isLoading || !data ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
        ) : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">
            {t('positions.empty_closed')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
                  <tr>
                    <Th className="text-left px-5">{t('dashboard.open_positions.table.symbol')}</Th>
                    <Th className="text-left">{t('dashboard.open_positions.table.side')}</Th>
                    <Th className="text-right">{t('dashboard.open_positions.table.entry')}</Th>
                    <Th className="text-right">{t('positions.table.exit')}</Th>
                    <Th className="text-right">{t('positions.table.pnl')}</Th>
                    <Th className="text-left">{t('positions.table.exit_reason')}</Th>
                    <Th className="text-right">{t('positions.table.duration')}</Th>
                    <Th className="text-right px-5">{t('positions.table.closed')}</Th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {data.items.map((p) => (
                    <ClosedRow
                      key={p.id}
                      pos={p}
                      onClick={() => onOpenDetail(p.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
              loading={isFetching}
            />
          </>
        )}
      </div>
    </section>
  )
}

function ClosedRow({
  pos, onClick,
}: {
  pos: ClosedPosition
  onClick: () => void
}) {
  const { t } = useTranslation('app')
  const pnl = pos.realized_pnl
  const pct = pos.realized_pnl_pct
  const pnlTone = pnl == null
    ? 'text-textFaint'
    : pnl >= 0 ? 'text-pulse' : 'text-rose'

  return (
    <tr
      onClick={onClick}
      className="border-b border-line/40 hover:bg-elevated/30 cursor-pointer transition-colors"
    >
      <td className="px-5 py-2 font-semibold">{pos.symbol}</td>
      <td className="py-2">
        <span className={cn(
          'pill',
          pos.direction === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose',
        )}>
          {t(`dashboard.open_positions.side.${pos.direction}`)}
        </span>
      </td>
      <td className="text-right py-2 text-textDim">
        {formatNumber(pos.entry_price, 4)}
      </td>
      <td className="text-right py-2">
        {pos.exit_price != null ? formatNumber(pos.exit_price, 4) : '—'}
      </td>
      <td className={cn('text-right py-2', pnlTone)}>
        {pnl != null ? formatUsd(pnl, { sign: true }) : '—'}
        {pct != null && (
          <span className="ml-1 text-textFaint">
            {formatPct(pct, { sign: true })}
          </span>
        )}
      </td>
      <td className="py-2">
        {pos.exit_reason ? (
          <span className="pill bg-elevated text-textDim">
            {t(`exit_reason.${pos.exit_reason}`, { defaultValue: pos.exit_reason })}
          </span>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>
      <td className="text-right py-2 text-textFaint">
        {pos.duration_minutes != null
          ? formatShortDuration(pos.duration_minutes)
          : '—'}
      </td>
      <td className="text-right px-5 py-2 text-textFaint">
        {pos.closed_at ? timeAgo(pos.closed_at) : '—'}
      </td>
    </tr>
  )
}

function formatShortDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h < 24) return m ? `${h}h${m}` : `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d${h % 24}h`
}

/* ─── building blocks ─────────────────────────────────────────────── */

function Th({
  className, children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <th className={cn('py-2 font-medium', className)}>
      {children}
    </th>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-ink border border-line px-5 py-10 text-center text-textFaint text-sm">
      {children}
    </div>
  )
}
